import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Plus, Save } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface MedicalNote {
  id: string;
  patient_id: string;
  provider_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  provider_name?: string;
}

interface MedicalNotesCardProps {
  medicalNotes: MedicalNote[];
  patientId?: string;
}

export function MedicalNotesCard({ medicalNotes, patientId }: MedicalNotesCardProps) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Sort notes by date (newest first)
  const sortedNotes = [...(medicalNotes || [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const handleAddNote = async () => {
    if (!newNoteContent.trim() || !profile?.id || !patientId) return;
    
    setIsSaving(true);
    
    try {
      const { data, error } = await supabase
        .from('medical_notes')
        .insert({
          patient_id: patientId,
          provider_id: profile.id,
          content: newNoteContent.trim(),
        })
        .select();
      
      if (error) throw error;
      
      // Reset form and refresh data
      setNewNoteContent("");
      setIsAddingNote(false);
      queryClient.invalidateQueries({ queryKey: ['patient-detail', patientId] });
      
    } catch (error) {
      console.error("Error saving medical note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Clinical Notes</CardTitle>
          <CardDescription>Medical notes and observations</CardDescription>
        </div>
        {patientId && profile?.id && !isAddingNote && (
          <Button 
            size="sm" 
            onClick={() => setIsAddingNote(true)}
            className="ml-auto"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Note
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isAddingNote && (
          <div className="space-y-2 border rounded-md p-3 bg-gray-50">
            <h3 className="text-sm font-medium">New Clinical Note</h3>
            <Textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Enter clinical observations, diagnosis, treatment plan, etc."
              className="min-h-[120px]"
            />
            <div className="flex justify-end space-x-2 pt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setIsAddingNote(false);
                  setNewNoteContent("");
                }}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleAddNote}
                disabled={!newNoteContent.trim() || isSaving}
              >
                <Save className="w-4 h-4 mr-1" />
                {isSaving ? "Saving..." : "Save Note"}
              </Button>
            </div>
          </div>
        )}

        {sortedNotes.length > 0 ? (
          <div className="space-y-4">
            {sortedNotes.map((note) => (
              <div key={note.id} className="border rounded-md p-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="text-sm text-gray-500">
                      {formatDate(note.created_at)}
                    </p>
                    {note.provider_name && (
                      <p className="text-sm font-medium">
                        Dr. {note.provider_name}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{note.content}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No clinical notes available</p>
            {!isAddingNote && patientId && profile?.id && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setIsAddingNote(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add First Clinical Note
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
